import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useConfig } from "@/context/ConfigContext";
import { useNavigate, Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Recycle, ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { formatError } from "@/lib/api";

const BG_IMAGE = "https://customer-assets.emergentagent.com/job_rifiuti-pesati/artifacts/jq26pxzd_municipio-caltavuturo-2.jpg";

export default function LoginPage() {
  const { login, user } = useAuth();
  const cfg = useConfig();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  if (user && !user.must_change_password) return <Navigate to="/app" replace />;
  if (user && user.must_change_password) return <Navigate to="/change-password" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email.trim(), password);
      toast.success(`Benvenuto, ${u.name}`);
      if (u.must_change_password) return nav("/change-password");
      if (u.role === "admin") return nav("/admin/users");
      if (u.role === "operator") return nav("/operator");
      return nav("/dashboard");
    } catch (err) {
      toast.error(formatError(err));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-5">
      {/* Left illustrative panel */}
      <div className="hidden lg:flex lg:col-span-3 relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${BG_IMAGE})` }}
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(0,82,163,0.92) 0%, rgba(15,81,50,0.85) 100%)" }} />
        <div className="relative z-10 flex flex-col justify-between p-14 text-white w-full">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-white/15 border border-white/20 backdrop-blur flex items-center justify-center">
              <Recycle className="w-6 h-6" />
            </div>
            <div>
              <div className="font-display font-semibold">{cfg.comune_name} · Portale Rifiuti</div>
              <div className="text-xs opacity-80 uppercase tracking-widest">Servizio sperimentale</div>
            </div>
          </div>
          <div className="max-w-lg">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest bg-white/10 border border-white/20 px-3 py-1.5 rounded-full mb-5">
              <ShieldCheck className="w-3.5 h-3.5" /> Portale istituzionale
            </div>
            <h1 className="font-display text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05]">
              Conferisci, pesa, ottieni lo sconto TARI.
            </h1>
            <p className="mt-5 text-white/85 leading-relaxed">
              Un servizio sperimentale per la gestione delle quantità indicative di rifiuti conferiti presso i punti di raccolta comunali. Ogni conferimento viene tracciato, verificato e trasformato in un beneficio reale sulla bolletta.
            </p>
          </div>
          <div className="text-xs text-white/60">© {new Date().getFullYear()} {cfg.comune_name}</div>
        </div>
      </div>

      {/* Right login form */}
      <div className="lg:col-span-2 flex items-center justify-center p-6 lg:p-12 bg-background">
        <Card className="w-full max-w-md border-gray-200 shadow-sm">
          <CardContent className="p-8">
            <div className="lg:hidden mb-6 flex items-center gap-2">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Recycle className="w-5 h-5 text-primary" />
              </div>
              <div className="font-display font-semibold">{cfg.comune_name} · Rifiuti</div>
            </div>
            <h2 className="font-display text-2xl font-semibold">Accedi</h2>
            <p className="text-sm text-muted-foreground mt-1">Utilizza le credenziali fornite dal Comune.</p>

            <form onSubmit={submit} className="mt-7 space-y-4">
              <div>
                <Label htmlFor="email" className="text-xs uppercase tracking-wider text-gray-600">Email</Label>
                <Input
                  id="email" type="email" required autoFocus
                  data-testid="login-email-input"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="nome@esempio.it" className="mt-1.5"
                />
              </div>
              <div>
                <Label htmlFor="pw" className="text-xs uppercase tracking-wider text-gray-600">Password</Label>
                <Input
                  id="pw" type="password" required
                  data-testid="login-password-input"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  className="mt-1.5"
                />
              </div>
              <Button type="submit" disabled={busy} data-testid="login-submit-btn" className="w-full rounded-full h-11 font-medium">
                {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Entra
              </Button>
            </form>

            <div className="mt-8 pt-5 border-t border-gray-200 text-xs text-muted-foreground">
              Le utenze vengono create dal back office comunale. Al primo accesso ti verrà chiesto di impostare una nuova password.
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
